import { useCallback, useEffect, useState } from "react";
import { advisoryApi, type Withdrawal } from "../advisoryApi";
import { dateOnly, money } from "../format";
import { SaveBar, Seg } from "../ui";
import { fromMonth, useSaver, type StepProps } from "./common";

const FREQ: Record<string, string> = { monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

/**
 * Wealth plan — planned withdrawals (QP: global-wealth-plan + dialog). Optional
 * on this flow: written to the advice and shown in the forecast, but never a
 * submit gate. Listed, added and removed here.
 */
export function WealthPlanStep({ sessionId, readOnly, onSaved, currency }: Pick<StepProps, "sessionId" | "readOnly" | "onSaved" | "currency">) {
  const [rows, setRows] = useState<Withdrawal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const saver = useSaver(onSaved);
  const [name, setName] = useState("");
  const [schedule, setSchedule] = useState<"one_time" | "recurring">("one_time");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [freq, setFreq] = useState("monthly");
  const [end, setEnd] = useState("");

  const reload = useCallback(() => {
    advisoryApi
      .wealthPlan(sessionId)
      .then((d) => {
        setRows(d.data);
        setErr(null);
      })
      .catch((e) => setErr((e as Error).message));
  }, [sessionId]);
  useEffect(reload, [reload]);

  const valid = name.trim() !== "" && Number(amount) > 0 && /^\d{4}-\d{2}$/.test(date);

  async function add() {
    if (!valid) return;
    const body =
      schedule === "one_time"
        ? { name: name.trim(), scheduleType: "one_time", oneTimeAmount: Number(amount), oneTimeDate: fromMonth(date) }
        : {
            name: name.trim(),
            scheduleType: "recurring",
            recurringAmount: Number(amount),
            recurringFrequency: freq,
            recurringStartDate: fromMonth(date),
            ...(end ? { recurringEndDate: fromMonth(end) } : {}),
          };
    const r = await saver.run(() => advisoryApi.addWithdrawal(sessionId, body));
    if (r) {
      setName("");
      setAmount("");
      setDate("");
      setEnd("");
      reload();
    }
  }

  async function remove(w: Withdrawal) {
    const r = await saver.run(() => advisoryApi.deleteWithdrawal(sessionId, w.id));
    if (r) reload();
  }

  return (
    <section className="adv-step-card">
      <p className="muted">
        Money the client plans to take out along the way — a cabin renovation, a yearly top-up to a child's account. It is
        included in the forecast; the advice can be submitted without it.
      </p>
      {err && <div className="banner-error">{err}</div>}
      {rows === null && !err ? (
        <p className="muted">Loading the wealth plan…</p>
      ) : rows && rows.length > 0 ? (
        <table className="table adv-table-compact">
          <thead>
            <tr>
              <th>Withdrawal</th>
              <th>When</th>
              <th className="num">Amount</th>
              {!readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.id}>
                <td className="strong">{w.name}</td>
                <td>
                  {w.scheduleType === "one_time"
                    ? dateOnly(w.oneTimeDate)
                    : `${FREQ[w.recurringFrequency ?? ""] ?? w.recurringFrequency} from ${dateOnly(w.recurringStartDate)}${w.recurringEndDate ? ` to ${dateOnly(w.recurringEndDate)}` : ""}`}
                </td>
                <td className="num">{money(w.scheduleType === "one_time" ? w.oneTimeAmount : w.recurringAmount, currency)}</td>
                {!readOnly && (
                  <td>
                    <div className="cell-actions"><button type="button" className="link-action danger" onClick={() => remove(w)} disabled={saver.saving}>
                      Remove
                    </button>
                  </div></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No planned withdrawals.</p>
      )}

      {!readOnly && (
        <div className="adv-subsection">
          <span className="adv-label">Add a withdrawal</span>
          <div className="field-row">
            <label className="field">
              <span>Name</span>
              <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cabin renovation" />
            </label>
            <div className="field">
              <span>Schedule</span>
              <Seg
                value={schedule}
                options={[
                  { value: "one_time", label: "One-time" },
                  { value: "recurring", label: "Recurring" },
                ]}
                onChange={setSchedule}
                label="Withdrawal schedule"
              />
            </div>
          </div>
          <div className="field-row">
            <label className="field">
              <span>Amount ({currency})</span>
              <input type="number" min={0} step={1000} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="field">
              <span>{schedule === "one_time" ? "Month" : "Start month"}</span>
              <input type="month" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            {schedule === "recurring" && (
              <>
                <label className="field">
                  <span>Frequency</span>
                  <select value={freq} onChange={(e) => setFreq(e.target.value)}>
                    {Object.entries(FREQ).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>End month</span>
                  <input type="month" value={end} onChange={(e) => setEnd(e.target.value)} />
                </label>
              </>
            )}
          </div>
          <SaveBar onSave={add} saving={saver.saving} dirty={valid} error={saver.error} readOnly={readOnly} label="Add withdrawal" note="Name, amount and month are needed." />
        </div>
      )}
    </section>
  );
}
